import {
  alpha,
  createTheme,
  type Palette,
  type Theme,
  type ThemeOptions,
  type TypeText,
} from '@mui/material/styles';
import type { CsTier } from '@/shared/theme/customDropdowns';

/**
 * Chart-specific tokens, additive to the standard MUI palette.
 *
 * `categorical` is a fixed, ordered palette for open-ended dimensions
 * (stage name, region code, source) where the number of distinct values
 * isn't known ahead of time — callers hash a stable key (never an array
 * index or a raw label string) into this array so a category keeps the
 * same color across re-sorts and filter changes. Binary/semantic pairs
 * (won/lost, on-track/at-risk) must keep using `palette.success`/`error`
 * directly rather than this array — a hash-assigned color would strip a
 * convention every user already reads at a glance.
 *
 * Scoped to the dashboard module today (consumed via `theme.chart.*`);
 * promote to a shared design-system token only if another module needs
 * the same categorical-coloring problem.
 */
declare module '@mui/material/styles' {
  interface Theme {
    chart: {
      categorical: readonly string[];
    };
  }
  interface ThemeOptions {
    chart?: {
      categorical: readonly string[];
    };
  }
}

/**
 * Dashboard V2 UI tokens — additive, sibling to `chart` above (deliberately
 * NOT folded into it: these have nothing to do with chart rendering).
 *
 * `placeholderBorder`/`placeholderHoverFill`/`placeholderIconBg` style the
 * "Add KPI" / "Build Your Widget" empty-state tiles (app.zip
 * `--dash-border`/`--blue-faint`/`--blue-light`). `kpiCardBg` is a distinct
 * token for the real KPI widget card's own background (`chart_type ===
 * 'kpi'`) — a different surface reusing an adjacent part of the same
 * palette, not the same token reused for two purposes.
 *
 * Dark-mode values are NOT a literal copy of the light-mode hex — same
 * "diverge deliberately, preserve the relative relationship" precedent as
 * `DARK_CATEGORICAL` below, since these are functional UI surfaces shown to
 * every user in dark mode (unlike `BrandMark`'s gradient, the one existing
 * token that stays genuinely light-only by design).
 */
declare module '@mui/material/styles' {
  interface Theme {
    dashboardUi: {
      placeholderBorder: string;
      placeholderHoverFill: string;
      placeholderIconBg: string;
      kpiCardBg: string;
    };
  }
  interface ThemeOptions {
    dashboardUi?: {
      placeholderBorder: string;
      placeholderHoverFill: string;
      placeholderIconBg: string;
      kpiCardBg: string;
    };
  }
}

/** A tinted pill or button: a fill with the ink drawn on it. */
export interface PillPair {
  readonly fill: string;
  readonly ink: string;
}

/** One `Client Tier` medallion variant (Figma component set `254:341`). */
export interface TierMedallionTier {
  /** CSS gradient for the 54px ring. Apply as `background`, never `bgcolor`. */
  readonly ring: string;
  /** CSS gradient for the label tab. */
  readonly tab: string;
  /**
   * The tab's Figma width in px. The tab gradient's angle is exact only at
   * this width (it depends on the box's aspect ratio), so the consumer pins
   * the tab to it rather than letting it hug the label.
   */
  readonly tabWidth: number;
}

export interface TierMedallionTokens {
  /** Figma's inner highlight on the ring and the tab, as a CSS box-shadow. */
  readonly highlight: string;
  /** Label text on the metal. Black in both modes: it sits on the gradient. */
  readonly labelInk: string;
  readonly tiers: Readonly<Record<CsTier, TierMedallionTier>>;
}

/**
 * Figma QFly-v2 tokens (CST-41; requirements-client-success-tab-redesign
 * §4.6 F1 and F7). The owner asked for the client page and the CS tab to
 * match Figma node `276:3946` exactly, so the design's colours are added here
 * as tokens, each with a dark value of our own (the design has no dark
 * frame). Components reference these names and never carry the hex.
 *
 * - They exist only on `qflyLightTheme` / `qflyDarkTheme`. A test that
 *   renders a consumer must wrap it in one of them; MUI's default theme has
 *   none of these keys.
 * - Prefer `sx` string paths (`color: 'text.body'`, `bgcolor:
 *   'stage.won.fill'`, `borderColor: 'border.sectionRule'`). A misspelt path
 *   is emitted as invalid CSS with no error, so the tests pin every path. Use
 *   `border: 1, borderColor: 'border.x'`, never `border: '1px solid
 *   border.x'`, which does not resolve.
 * - Every ink is opaque and meets WCAG AA (4.5:1) on its intended surfaces in
 *   both modes. Figma draws several runs at 70–90% opacity; those are dropped
 *   deliberately, because the same ink at reduced opacity fails AA.
 * - `palette.success` / `warning` / `error` stay fill and icon colours. Two of
 *   them fail AA as text in light mode, so text uses `text.success` /
 *   `text.warning` / `text.error` below.
 */
declare module '@mui/material/styles' {
  interface TypeText {
    /** Figma `text/Heading`: definition values in CS cards. Sits between
     *  `text.primary` and `text.secondary`. */
    heading: string;
    /** Figma `text/body`: row titles, narrative and notes text, sub-headings. */
    body: string;
    /** Figma `text/tertiary`: the info icon, quiet empty-state lines. */
    tertiary: string;
    /** Figma `text/links`. Dark mode needs its own blue: `primary.main` fails
     *  AA as text on the dark surfaces. */
    link: string;
    /** Figma `caption/color/icon/success`. Intended for `background.paper`
     *  and `background.default` only — it is 4.49:1 on `grey.50`. */
    success: string;
    /** Figma `caption/color/text/success`: the header's revenue figure. */
    successStrong: string;
    /** Figma `label/color/text/danger`. Named `error` for MUI's vocabulary. */
    error: string;
    /** Figma `Default/Amber/80`: risk "Warning" and the Dismiss ink. */
    warning: string;
  }
  interface Palette {
    border: {
      /** Figma `input/color/border/default`. Decorative (the activity
       *  rail); it is not an input boundary and does not need 3:1. */
      readonly input: string;
      /** Figma `Default/Cool Grey/20`: the rule between card sub-sections. */
      readonly sectionRule: string;
    };
    table: {
      /** Figma `table/row-rule`: the rule under each body row. */
      readonly rowRule: string;
      /** Figma `table/row-name-ink`: the first column's name. */
      readonly rowNameInk: string;
    };
    /** Figma's stage ramp, which the design also uses for severity and
     *  health pills. The dashboard's `stagePillColors.ts` holds the same
     *  light values; a drift test there pins them together. */
    stage: { readonly won: PillPair; readonly proposing: PillPair; readonly lost: PillPair };
    /** The header's micro-pills (Region, Rating, health). Same family as
     *  `softChipColors()`, but with the exact pairs Figma draws. */
    softChip: { readonly green: PillPair; readonly amber: PillPair; readonly blue: PillPair };
    /** Figma's `Flag Actions` buttons. The border is drawn in the ink. */
    flagAction: {
      readonly acknowledge: PillPair;
      readonly dismiss: PillPair;
      readonly resolve: PillPair;
    };
  }
  interface PaletteOptions {
    border?: Palette['border'];
    table?: Palette['table'];
    stage?: Palette['stage'];
    softChip?: Palette['softChip'];
    flagAction?: Palette['flagAction'];
  }
  interface Theme {
    tierMedallion: TierMedallionTokens;
  }
  interface ThemeOptions {
    tierMedallion?: TierMedallionTokens;
  }
}

/**
 * Qfly MUI theme — global visual-redesign foundation (2026-08), now with a
 * light + dark palette pair.
 *
 * Colors are ported 1:1 from the approved design source (`app.zip` / Figma
 * "QFly" file), which ships both a light default and a `data-theme="dark"`
 * variant. Both are pre-built once at module scope (`qflyLightTheme` /
 * `qflyDarkTheme`) and looked up by `createQflyTheme(mode)` — never rebuilt
 * per call, so switching modes at runtime is a reference swap, not a
 * `createTheme()` call on every render.
 *
 * Intentional, documented exception: `modules/auth/LoginPage.tsx` keeps
 * its own separate, recently-approved Figma palette (`#0056ce` primary) —
 * that page already carries an explicit code comment flagging the two
 * palettes as an open reconciliation question for the business to decide,
 * so this redesign does not silently resolve it. `BrandMark.tsx`'s gradient
 * (inside that component) and `tierMedallion` (on the theme) are also
 * deliberately unchanged across modes — a logotype that shifts hue
 * with the theme stops being the brand mark (see the source CSS's own note
 * on `--brand-grad-from/to`), and a gold medal that shifts hue stops being
 * gold.
 *
 * Tailwind (`tailwind.config.ts`) mirrors the brand/neutral tokens here —
 * update both together (a few shell components still mix Tailwind utility
 * classes with MUI `sx`). The Figma QFly-v2 tokens below are MUI-only: no
 * Tailwind class consumes them.
 */

// ── Chart categorical palette ────────────────────────────────────────────
// Light: ported 1:1 from the design source's `--cat-1..8` (unchanged from
// its own dark block — the source keeps these identical across themes).
// Dark: this app deliberately DIVERGES from the source here. The source's
// mid-lightness pastels are close enough to washing out against a near-black
// canvas that the frontend swarm review flagged it as a risk; these 8 use the
// same hue order (green, violet, orange, cyan, magenta, yellow, blue, pink)
// at higher saturation so they read clearly on a dark surface instead.
const LIGHT_CATEGORICAL: readonly string[] = [
  '#74dd8c',
  '#a574dd',
  '#dd9f74',
  '#74cedd',
  '#dd74d5',
  '#ddd974',
  '#7492dd',
  '#dd748e',
];

const DARK_CATEGORICAL: readonly string[] = [
  '#34d399',
  '#a78bfa',
  '#fb923c',
  '#22d3ee',
  '#f472b6',
  '#fbbf24',
  '#60a5fa',
  '#fb7185',
];

// ── Dashboard V2 UI tokens ────────────────────────────────────────────────
// Light: ported 1:1 from app.zip's `--dash-border`/`--blue-faint`/
// `--blue-light`/`--kpi-card-bg`. Dark: no source dark spec exists for these
// (app.zip ships light-mode-only for this specific set) — values below are
// this app's own choice, preserving the same relative tint/contrast
// relationship against `darkPalette`'s background/divider rather than
// reusing the light hexes verbatim (see the `dashboardUi` module-
// augmentation comment above for why).
const LIGHT_DASHBOARD_UI = {
  placeholderBorder: '#99bff5',
  placeholderHoverFill: '#f0f6ff',
  placeholderIconBg: '#ebf2ff',
  kpiCardBg: '#fcfcfc',
} as const;

const DARK_DASHBOARD_UI = {
  placeholderBorder: '#3a5c96',
  placeholderHoverFill: '#182636',
  placeholderIconBg: '#1f3350',
  kpiCardBg: '#1c2129',
} as const;

// ── Figma QFly-v2 tokens (CST-41) ─────────────────────────────────────────
// Light: the Figma variables verbatim, except where one fails WCAG AA for its
// use (requirements §4.6 F7); each substitution is commented where it is made.
// Figma's muted grey `#8a9099` gets no token at all: it is 3.22:1 on white
// (2.15:1 at the 70% opacity Figma draws it at), so consumers use `grey.500`
// (4.83:1). The nearest passing shade, `#717781`, falls below 4.5:1 on
// `background.default`, so the existing step wins.
// Dark: ours — the design has no dark frame. Each value keeps a light-mode
// relationship rather than inverting a hex: the text ladder (body < heading <
// table.rowNameInk), Critical >= High >= Low prominence for the stage pills,
// and the soft chips sitting quieter than the stage pills. The tests assert
// those orderings and AA in both modes, so a later tweak cannot quietly
// break one.
type QflyTextTokens = Pick<
  TypeText,
  'heading' | 'body' | 'tertiary' | 'link' | 'success' | 'successStrong' | 'error' | 'warning'
>;
type QflyPaletteTokens = Pick<Palette, 'border' | 'table' | 'stage' | 'softChip' | 'flagAction'>;

const LIGHT_TEXT_TOKENS = {
  heading: '#34393f',
  body: '#4d5258',
  tertiary: '#696f77',
  link: '#0060e5', // = primary.main
  success: '#108548',
  successStrong: '#1d522f',
  error: '#a61d1d',
  warning: '#856300',
} as const satisfies QflyTextTokens;

const DARK_TEXT_TOKENS = {
  heading: '#ccd3dc', // dark grey.700
  body: '#b3bcc8', // dark grey.600
  tertiary: '#9aa5b3', // dark grey.500
  link: '#8ab4ff', // one dark blue ink: also acknowledge and the blue chip
  success: '#4ade80', // = dark success.main
  successStrong: '#86efac',
  error: '#f87171',
  warning: '#f0b429', // = dark warning.main; also the dismiss ink
} as const satisfies QflyTextTokens;

const LIGHT_FIGMA_TOKENS = {
  border: { input: '#ccd1d9', sectionRule: '#dde1e6' },
  table: { rowRule: '#eef1f4', rowNameInk: '#2e3134' },
  stage: {
    won: { fill: '#bceadb', ink: '#0f6e56' },
    proposing: { fill: '#fbd89e', ink: '#854f0b' },
    lost: { fill: '#f7c7b6', ink: '#993c1d' },
  },
  softChip: {
    // Figma's ink is #11813b: 4.27:1 on this fill. #107c39 is the nearest
    // shade that passes (4.55:1), moving lightness only.
    green: { fill: '#e1f2e6', ink: '#107c39' },
    amber: { fill: '#fef3c7', ink: '#5b4309' },
    blue: { fill: '#ebf2ff', ink: '#0060e5' },
  },
  flagAction: {
    // Opaque: Figma's #d9e7fb at 50% opacity, composited over white.
    acknowledge: { fill: '#ecf3fd', ink: '#0060e5' },
    // Figma draws the label #ad8200 (3.37:1 on this fill) inside a #856300
    // border. The label takes the border's colour, a Figma value that
    // passes (5.32:1), rather than an invented nearest shade.
    dismiss: { fill: '#fffaeb', ink: '#856300' },
    // Figma draws the label at 90% opacity (4.21:1); opaque it is 5.11:1.
    resolve: { fill: '#eff6f1', ink: '#217645' },
  },
} as const satisfies QflyPaletteTokens;

const DARK_FIGMA_TOKENS = {
  border: { input: '#454e5a', sectionRule: '#2e353f' },
  table: { rowRule: '#222831', rowNameInk: '#e2e7ed' },
  stage: {
    won: { fill: '#16352c', ink: '#7fdcbc' },
    proposing: { fill: '#4a3510', ink: '#f8c76b' },
    // Deeper than a straight translation, so Critical (lost) stays the most
    // prominent severity pill in dark mode as it is in light.
    lost: { fill: '#5e2e1f', ink: '#f4a58a' },
  },
  softChip: {
    green: { fill: '#1c2d24', ink: '#6ee7a0' },
    amber: { fill: '#2a2617', ink: '#fcd34d' },
    blue: { fill: '#1e273a', ink: '#8ab4ff' },
  },
  flagAction: {
    // Opaque: alpha(dark primary.main, 0.15) composited over the dark paper.
    acknowledge: { fill: '#19273c', ink: '#8ab4ff' },
    dismiss: { fill: '#2b2410', ink: '#f0b429' },
    resolve: { fill: '#12261a', ink: '#5cc283' },
  },
} as const satisfies QflyPaletteTokens;

// ── Client Tier medallion (Figma component set 254:341) ───────────────────
// Converted exactly from each paint's gradientTransform [[a, b, c], ...]. The
// CSS angle comes from (a, b) scaled by the box's width and height; a stop at
// Figma position s lands at 0.5 + (s − ((a + b) / 2 + c)) / (|a| + |b|), which
// does not depend on the box. So the platinum and silver tabs share their
// ring's stops and differ only in angle, and each tab's angle holds only at
// its Figma width (`tabWidth`). Stops are given to two decimals; the source
// rounds Figma's own positions to whole percent.
// Mode-invariant on purpose: this is a metal object, not a surface. It is the
// first mode-invariant token on the theme (BrandMark's gradient is constant
// inside its component, not here).
const TIER_MEDALLION: TierMedallionTokens = {
  highlight: 'inset 0 4px 6px rgba(255, 255, 255, 0.6)',
  labelInk: '#000000',
  tiers: {
    platinum: {
      ring: 'linear-gradient(145.01deg, #cda0ff 23.98%, #8489f5 45.57%, #cdf1ff 60.22%, #cce8fe 64.07%, #b591e9 82.58%)',
      tab: 'linear-gradient(163.64deg, #cda0ff 23.98%, #8489f5 45.57%, #cdf1ff 60.22%, #cce8fe 64.07%, #b591e9 82.58%)',
      tabWidth: 31,
    },
    gold: {
      ring: 'linear-gradient(143.13deg, #d99414 14.29%, #fbe67b 33.93%, #e0db74 47.02%, #f7d14e 60.12%, #d4a041 73.81%)',
      tab: 'linear-gradient(162.22deg, #8c421d 15.37%, #fbe67b 34.72%, #fcfbe7 47.62%, #f7d14e 60.53%, #d4a041 74.02%)',
      tabWidth: 29,
    },
    silver: {
      ring: 'linear-gradient(137.8deg, #698194 16.57%, #eaeff3 35.45%, #abbac7 49.18%, #ffffff 60.34%, #d4dee5 73.22%, #95a9b5 82.66%, #bccad7 96.39%)',
      tab: 'linear-gradient(160.88deg, #698194 16.57%, #eaeff3 35.45%, #abbac7 49.18%, #ffffff 60.34%, #d4dee5 73.22%, #95a9b5 82.66%, #bccad7 96.39%)',
      tabWidth: 34,
    },
    bronze: {
      ring: 'linear-gradient(139.86deg, #9e8976 -19.1%, #7a5e50 0.84%, #f6d0ab 23.92%, #9d774e 44.91%, #c99b70 71.14%, #795f52 85.83%)',
      tab: 'linear-gradient(175.35deg, #9e8976 -59.42%, #7a5e50 -9.02%, #f6d0ab 49.34%, #9d774e 102.39%, #c99b70 168.7%, #795f52 205.84%)',
      tabWidth: 41,
    },
  },
};

const lightPalette: ThemeOptions['palette'] = {
  mode: 'light',
  ...LIGHT_FIGMA_TOKENS,
  primary: {
    // dark-mode source values: main #2568d6, light #4d94ff, dark #3d84f5
    main: '#0060e5',
    light: '#3b82f6',
    dark: '#004fbc',
    contrastText: '#ffffff',
  },
  secondary: {
    // No second brand hue exists in the source — it uses one accent
    // (blue) plus neutral ink everywhere. Kept as a dark-neutral so
    // existing `secondary.main` consumers (e.g. avatar fallback bg)
    // still render a sensible, on-brand color rather than inventing one.
    // The source never gives this a dark-mode counterpart either — it
    // stays the same dark neutral in both palettes.
    main: '#1f2937',
    contrastText: '#ffffff',
  },
  success: { main: '#16a34a' }, // dark: #4ade80
  warning: { main: '#d97706' }, // dark: #f0b429
  error: { main: '#dc2626' }, // dark: #c92a2a
  info: { main: '#0c857b' }, // dark: #3fd6c4 (source "teal" — the one informational accent distinct from primary blue)
  divider: '#e4e9f0', // --card-border
  background: {
    default: '#fafcff', // --page-bg
    paper: '#ffffff', // --surface
  },
  text: {
    ...LIGHT_TEXT_TOKENS,
    primary: '#111827', // --g900
    secondary: '#374151', // --g700
    disabled: '#9ca3af', // --g400
  },
  grey: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
  },
};

const darkPalette: ThemeOptions['palette'] = {
  mode: 'dark',
  ...DARK_FIGMA_TOKENS,
  primary: {
    main: '#2568d6',
    light: '#4d94ff',
    dark: '#3d84f5',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#1f2937',
    contrastText: '#ffffff',
  },
  success: { main: '#4ade80' },
  warning: { main: '#f0b429' },
  error: { main: '#c92a2a' },
  info: { main: '#3fd6c4' },
  divider: '#2a313a', // --card-border (dark)
  background: {
    default: '#0f1215', // --page-bg (dark)
    paper: '#171b21', // --surface (dark)
  },
  text: {
    ...DARK_TEXT_TOKENS,
    primary: '#f4f6f9', // --g900 (dark)
    secondary: '#ccd3dc', // --g700 (dark)
    disabled: '#67717e', // --g400 (dark)
  },
  grey: {
    50: '#1b2027',
    100: '#222831',
    200: '#2e353f',
    300: '#454e5a',
    400: '#67717e',
    500: '#9aa5b3',
    600: '#b3bcc8',
    700: '#ccd3dc',
    800: '#e2e7ed',
    900: '#f4f6f9',
  },
};

// ── Elevation (Paper / Menu / Popover / Dialog) ──────────────────────────
// A black drop-shadow does almost no visual work against an already-dark
// page, so dark mode pairs a deepened, more-opaque shadow with a subtle
// hairline border — the same treatment `MuiCard` already uses below.
// Values below are computed once per call (not duplicated per override) and
// match the design source's own solved dark-mode shadow tokens
// (`--shadow-pop` / `--shadow-modal` in `styles.css`).
const popShadow = (theme: Theme): string =>
  theme.palette.mode === 'dark'
    ? '0 8px 24px rgba(0, 0, 0, 0.55)' // --shadow-pop (dark)
    : '0 8px 24px rgba(0, 0, 0, 0.08)'; // --shadow-pop (light)

const modalShadow = (theme: Theme): string =>
  theme.palette.mode === 'dark'
    ? '0 32px 64px rgba(0, 0, 0, 0.66)' // --shadow-modal (dark)
    : '0 32px 64px rgba(0, 0, 0, 0.18)'; // --shadow-modal (light)

/** Dark-only hairline border, layered under the deepened shadow above. */
const elevationBorder = (theme: Theme): { border?: string } =>
  theme.palette.mode === 'dark' ? { border: `1px solid ${theme.palette.divider}` } : {};

// ── Shared, mode-agnostic options ────────────────────────────────────────
// Typography/shape/mixins carry no color literals, and every `components`
// override below already reads its colors from the `theme` callback MUI
// passes in — so a single definition, reused for both `createTheme()` calls,
// automatically resolves to the right palette for whichever theme it ends
// up attached to. Only `palette`, `chart` and `dashboardUi` differ per mode
// (see `buildThemeOptions` below); `tierMedallion` is mode-invariant, so it
// lives here, once.
const sharedOptions: Omit<ThemeOptions, 'palette' | 'chart' | 'dashboardUi'> = {
  tierMedallion: TIER_MEDALLION,
  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
    fontSize: 14,
    h1: { fontSize: '1.802rem', fontWeight: 800, letterSpacing: '-0.03em' },
    h2: { fontSize: '1.602rem', fontWeight: 800, letterSpacing: '-0.03em' },
    h3: { fontSize: '1.424rem', fontWeight: 800, letterSpacing: '-0.025em' },
    h4: { fontSize: '1.266rem', fontWeight: 800, letterSpacing: '-0.02em' },
    h5: { fontSize: '1.125rem', fontWeight: 700 },
    h6: { fontSize: '1.125rem', fontWeight: 700, letterSpacing: '-0.015em' },
    body1: { fontSize: '1rem' },
    body2: { fontSize: '0.889rem' },
    caption: { fontSize: '0.79rem' },
    button: { fontSize: '0.889rem', fontWeight: 600, textTransform: 'none' },
  },
  shape: {
    borderRadius: 8, // --rs — base radius for buttons/inputs/chips/nav rows
  },
  mixins: {
    // Matches the design source's shorter top bar. MUI's default `<Toolbar />`
    // (AppShell's two spacer Toolbars + TopBar's real one) all consume this
    // automatically — do not also add a per-component `minHeight` override,
    // that would defeat having one source of truth here.
    toolbar: { minHeight: 58 },
  },
  components: {
    // app.zip design parity (2026-08): a thin, unobtrusive scrollbar
    // (`::-webkit-scrollbar` in its styles.css) instead of the browser
    // default. Global — every scrollable region in the app picks it up,
    // matching app.zip everywhere a table/list/panel scrolls.
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        '*::-webkit-scrollbar': { width: 4, height: 4 },
        '*::-webkit-scrollbar-track': { background: 'transparent' },
        '*::-webkit-scrollbar-thumb': {
          background: theme.palette.grey[300],
          borderRadius: 2,
        },
      }),
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 8, fontWeight: 600 },
        sizeMedium: { height: 31, padding: '0 14px' },
        sizeSmall: { height: 26, padding: '0 10px', fontSize: '0.79rem' },
        containedPrimary: ({ theme }) => ({
          '&:hover': { backgroundColor: theme.palette.primary.dark },
          '&.Mui-disabled': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            opacity: 0.45,
          },
        }),
        // Text buttons are neutral grey whatever their `color` — the 2026-08
        // redesign's look — with one exception. This slot also beat
        // `color="warning"`, so the CS tab's "Flag Single-thread Risk" CTA,
        // the app's only text-variant warning button, rendered near-black
        // (CST-40). Warning now takes the AA amber ink, with its own hover so
        // the grey hover below cannot turn it near-black again.
        // Error and success stay grey here on purpose: dark mode has no
        // AA-passing red text yet, and those buttons sit on other
        // engagements' screens. They are a follow-up story, not this fix.
        text: ({ theme, ownerState }) =>
          ownerState.color === 'warning'
            ? {
                color: theme.palette.text.warning,
                '&:hover': {
                  color: theme.palette.text.warning,
                  backgroundColor: alpha(theme.palette.warning.main, theme.palette.action.hoverOpacity),
                  // Touch devices keep :hover after a tap; drop the tint there.
                  '@media (hover: none)': { backgroundColor: 'transparent' },
                },
              }
            : {
                color: theme.palette.grey[800],
                '&:hover': { backgroundColor: theme.palette.grey[100], color: theme.palette.grey[900] },
              },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 12, // --r
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
          backgroundImage: 'none',
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        elevation1: ({ theme }) => ({
          boxShadow: popShadow(theme), // --shadow-pop
          ...elevationBorder(theme),
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 999, fontWeight: 600 }, // --pill-radius — always fully rounded outside tables
        sizeSmall: { fontSize: '0.7rem' },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 8,
          '&.Mui-focused': {
            boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`, // --focus-halo
          },
        }),
      },
    },
    MuiAppBar: {
      styleOverrides: {
        colorInherit: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.secondary,
          boxShadow: 'none',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.background.default, // --page-bg
          borderRight: `1px solid ${theme.palette.divider}`,
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: ({ theme }) => ({
          fontSize: '0.625rem',
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: theme.palette.grey[500],
          backgroundColor: theme.palette.grey[50],
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
        }),
        body: ({ theme }) => ({
          fontSize: '0.889rem',
          color: theme.palette.grey[700],
          // app.zip design parity (2026-08 Clients reskin) — body rows get a
          // lighter divider than the header's grey[200] border above, so the
          // header band reads as a distinct row from the data underneath it.
          // Reuses the existing `divider` token (--card-border) rather than
          // adding a new one-off literal.
          borderBottom: `1px solid ${theme.palette.divider}`,
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) }, // --blue-faint
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: ({ theme }) => ({ backgroundColor: theme.palette.primary.main, height: 2 }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '1rem',
          color: theme.palette.grey[500],
          '&.Mui-selected': { color: theme.palette.primary.dark },
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: 16,
          boxShadow: modalShadow(theme), // --shadow-modal
          ...elevationBorder(theme),
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: 12,
          boxShadow: popShadow(theme), // --shadow-pop
          ...elevationBorder(theme),
        }),
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: 12,
          boxShadow: popShadow(theme), // --shadow-pop
          ...elevationBorder(theme),
        }),
      },
    },
  },
};

/**
 * Builds the full `createTheme()` options for one mode. Both `qflyLightTheme`
 * and `qflyDarkTheme` are threaded through this one function rather than two
 * hand-duplicated option objects — only `palette` and `chart.categorical`
 * actually vary by mode; everything else in `sharedOptions` is reused as-is.
 */
const buildThemeOptions = (mode: 'light' | 'dark'): ThemeOptions => ({
  chart: {
    categorical: mode === 'dark' ? DARK_CATEGORICAL : LIGHT_CATEGORICAL,
  },
  dashboardUi: mode === 'dark' ? DARK_DASHBOARD_UI : LIGHT_DASHBOARD_UI,
  palette: mode === 'dark' ? darkPalette : lightPalette,
  ...sharedOptions,
});

/** Built once at module scope — never rebuild inside `createQflyTheme`. */
export const qflyLightTheme: Theme = createTheme(buildThemeOptions('light'));
export const qflyDarkTheme: Theme = createTheme(buildThemeOptions('dark'));

/**
 * Zero-cost lookup, not a factory — both themes above are already built.
 * Calling `createTheme()` here on every render/mode-change would be wasteful
 * and would break referential stability for consumers (e.g. `useMemo`
 * dependents, or tests asserting the same theme instance is reused).
 */
export const createQflyTheme = (mode: 'light' | 'dark'): Theme =>
  mode === 'dark' ? qflyDarkTheme : qflyLightTheme;

/**
 * Back-compat alias — the 80+ existing consumers (mostly tests doing
 * `<ThemeProvider theme={qflyTheme}>`) need zero changes now that a dark
 * variant exists alongside it.
 */
export const qflyTheme: Theme = qflyLightTheme;
